import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";

export const saveDeck = async (
  name: string, 
  game: string, 
  data: any, 
  isPublic: boolean = false, 
  id?: string,
  gameFormatId?: number | string | null
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('User NOT authenticated');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('User profile NOT found');

    const numericGameId = !isNaN(Number(game)) && Number(game) > 0 ? Number(game) : null;
    const rawFormatId = gameFormatId ?? data?.format_id ?? data?.game_format_id;
    const numericGameFormatId = rawFormatId && !isNaN(Number(rawFormatId)) && Number(rawFormatId) > 0 ? Number(rawFormatId) : null;

    const deckPayload: any = {
      user_id: profile.id,
      name: name,
      game_id: numericGameId,
      game_format_id: numericGameFormatId,
      data: data,
      is_public: isPublic,
      updated_at: new Date().toISOString()
    };

    if (id) {
       const { error } = await supabase
         .from('user_decks')
         .update(deckPayload)
         .eq('id', id)
         .eq('user_id', profile.id);
       if (error) throw error;
       return id;
    } else {
       const newId = crypto.randomUUID();
       const { data: inserted, error } = await supabase
         .from('user_decks')
         .insert({ ...deckPayload, id: newId })
         .select()
         .maybeSingle();
       if (error) throw error;
       return inserted?.id || newId;
    }
  } catch (error) {
    console.error('Error in saveDeck:', error);
    throw error;
  }
};


export const listUserDecks = async (game?: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return [];

    let query = supabase
      .from('user_decks')
      .select(`
        id, 
        name, 
        game_id, 
        game_format_id, 
        is_public, 
        data, 
        created_at, 
        updated_at,
        games:game_id(id, name),
        game_formats:game_format_id(id, name)
      `)
      .eq('user_id', profile.id);
    
    if (game && !isNaN(Number(game))) {
      query = query.eq('game_id', Number(game));
    }

    const { data, error } = await query.order('updated_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((d: any) => ({
      ...d,
      user: {
        id: profile.id,
        auth_id: profile.auth_id,
        username: profile.username,
        fullname: profile.fullname,
        avatar: profile.avatar
      }
    }));
  } catch (error) {
    console.error('Error listUserDecks:', error);
    return [];
  }
};


export const getUserDeck = async (deckId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_decks')
      .select(`
        *,
        games:game_id(id, name),
        game_formats:game_format_id(id, name)
      `)
      .eq('id', deckId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    if (data.user_id) {
      let userProfile = null;
      try {
        const isNumeric = !isNaN(Number(data.user_id));
        if (isNumeric) {
          const { data: safeUsersById, error: rpcErr } = await (supabase.rpc('get_safe_users') as any)
            .select('id, username, fullname, avatar')
            .eq('id', Number(data.user_id))
            .limit(1);

          if (!rpcErr && safeUsersById && safeUsersById.length > 0) {
            userProfile = safeUsersById[0];
          }
        }
      } catch (rpcErr) {
        console.warn('Warning fetching safe user for deck via get_safe_users:', rpcErr);
      }

      // Fallback: direct table query if RPC was not available or user not found by id
      if (!userProfile) {
        const { data: uById } = await supabase
          .from('users')
          .select('id, username, fullname, avatar')
          .eq('id', data.user_id)
          .maybeSingle();
        userProfile = uById;

        if (!userProfile) {
          const { data: uByAuth } = await supabase
            .from('users')
            .select('id, username, fullname, avatar')
            .eq('auth_id', data.user_id)
            .maybeSingle();
          userProfile = uByAuth;
        }
      }

      if (userProfile) {
        return { ...data, user: userProfile };
      }
    }

    return data;
  } catch (error) {
    console.error('Error getUserDeck:', error);
    return null;
  }
};


export const deleteUserDeck = async (deckId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('Profile not found');

    const { error } = await supabase
      .from('user_decks')
      .delete()
      .eq('id', deckId)
      .eq('user_id', profile.id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleteUserDeck:', error);
    throw error;
  }
};

export interface PublicDeckFilter {
  gameId?: number | string | 'All';
  search?: string;
  sortBy?: 'recent' | 'name' | 'cards';
  format?: string;
  limit?: number;
  offset?: number;
}

export const listPublicDecks = async (options?: PublicDeckFilter) => {
  try {
    let query = supabase
      .from('user_decks')
      .select('*')
      .eq('is_public', true);

    if (options?.gameId && options.gameId !== 'All' && !isNaN(Number(options.gameId))) {
      query = query.eq('game_id', Number(options.gameId));
    }

    if (options?.search && options.search.trim()) {
      query = query.ilike('name', `%${options.search.trim()}%`);
    }

    if (options?.sortBy === 'name') {
      query = query.order('name', { ascending: true });
    } else {
      query = query.order('updated_at', { ascending: false });
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data: decks, error } = await query;
    if (error) throw error;
    if (!decks || decks.length === 0) return [];

    // Collect user_ids to batch fetch user profiles using RPC get_safe_users
    const userIds = Array.from(new Set(decks.map(d => d.user_id).filter(Boolean)));
    const usersMap: Record<string, any> = {};

    if (userIds.length > 0) {
      const numericIds = userIds
        .filter((id: any) => !isNaN(Number(id)))
        .map((id: any) => Number(id));
      const nonNumericIds = userIds
        .filter((id: any) => isNaN(Number(id)) || String(id).length > 20)
        .map(String);

      try {
        // Query safe users by numeric ID using get_safe_users RPC
        if (numericIds.length > 0) {
          const { data: safeUsersById, error: rpcErr } = await (supabase.rpc('get_safe_users') as any)
            .select('id, username, fullname, avatar')
            .in('id', numericIds);

          if (!rpcErr && safeUsersById && Array.isArray(safeUsersById)) {
            safeUsersById.forEach((u: any) => {
              if (u.id != null) {
                usersMap[u.id] = u;
                usersMap[String(u.id)] = u;
              }
            });
          } else if (rpcErr) {
            console.warn('Warning fetching safe users by id for decks:', rpcErr);
          }
        }
      } catch (err) {
        console.warn('Error fetching safe users for decks via RPC get_safe_users:', err);
      }

      // Fallback: direct table query if RPC had issues or missed any
      const missingUserIds = userIds.filter(uid => !usersMap[uid] && !usersMap[String(uid)]);
      if (missingUserIds.length > 0) {
        try {
          const missingNumeric = missingUserIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
          if (missingNumeric.length > 0) {
            const { data: usersById } = await supabase
              .from('users')
              .select('id, username, fullname, avatar')
              .in('id', missingNumeric);

            (usersById || []).forEach((u: any) => {
              if (u.id != null) {
                usersMap[u.id] = u;
                usersMap[String(u.id)] = u;
              }
            });
          }

          const stillMissing = missingUserIds.filter(uid => !usersMap[uid] && !usersMap[String(uid)]);
          if (stillMissing.length > 0) {
            const { data: usersByAuth } = await supabase
              .from('users')
              .select('id, auth_id, username, fullname, avatar')
              .in('auth_id', stillMissing.map(String));

            (usersByAuth || []).forEach((u: any) => {
              if (u.auth_id) {
                usersMap[u.auth_id] = u;
              }
              if (u.id != null) {
                usersMap[u.id] = u;
                usersMap[String(u.id)] = u;
              }
            });
          }
        } catch {
          // ignore fallback error
        }
      }
    }

    return decks.map(d => ({
      ...d,
      user: usersMap[d.user_id] || usersMap[String(d.user_id)] || null
    }));
  } catch (error) {
    console.error('Error in listPublicDecks:', error);
    return [];
  }
};

export const cloneDeck = async (deckId: string, customName?: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Usuário não autenticado');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('Perfil de usuário não encontrado');

    const sourceDeck = await getUserDeck(deckId);
    if (!sourceDeck) throw new Error('Deck de origem não encontrado');

    const newDeckName = customName || `${sourceDeck.name} (Cópia)`;
    const newId = crypto.randomUUID();
    const deckPayload = {
      id: newId,
      user_id: profile.id,
      name: newDeckName,
      game_id: sourceDeck.game_id,
      data: sourceDeck.data,
      is_public: false,
      updated_at: new Date().toISOString()
    };

    const { data: inserted, error } = await supabase
      .from('user_decks')
      .insert({
        ...deckPayload,
        game_format_id: sourceDeck.game_format_id || sourceDeck.data?.format_id || null
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    return inserted?.id || newId;
  } catch (error) {
    console.error('Error in cloneDeck:', error);
    throw error;
  }
};



